import { debounce } from 'lodash';
import { snapshot } from 'valtio';
import { getAppOwnersStore } from '../../../application-owners/api/applications-owners-api';
import { createSimpleAsyncAction } from 'async-utils';
import { Nullable } from 'common-types';
import { AppEvents } from 'common-events';
import {
  AppPages,
  getQueryString,
  navigate,
} from '../../../common/routing/app-navigator';
import { OneOfValues } from 'common-types';
import { calcDateRange } from '../../../app/store-actions/top-bar-store-actions';
import TopBarStore from '../../../app/stores/top-bar-store';
import ScanStore from '../../../new-scan/store/scan-store';
import {
  openSnackbar,
  closeSnackbar,
} from '../../../snackbar/actions/snackbar-actions';
import { trackAppcuesEvent } from '../../../telemetry/appcues';
import * as issuesStoreActions from '../../active-issues/store-actions/issue-store-actions';
import * as pipelineIssuesStoreActions from '../../pipeline-issues/store-actions/pipeline-issues-store-actions';
import IssuesStore from '../../active-issues/stores/issues-store';
import resolvedIssuesService from '../../resolved-issues/services';
import * as resolvedIssuesStoreActions from '../../resolved-issues/store-actions/resolved-issue-store-actions';
import ResolvedIssuesStore from '../../resolved-issues/stores/resolved-issues-store';
import { setStoreTopIssues } from '../../top-issues/store-actions/top-issues-store-actions';
import issuesService from '../services';
import {
  setExclusionsCategories,
  setSelectedAggs,
} from '../store-actions/issues-exclusions-store-actions';
import {
  Issue,
  IssuesOrder,
  IssueSortKey,
  LoadIssuesFiltersParams,
  LoadIssuesParams,
} from '../types/issues-types';
import { logger } from 'logging-utils';
import { getJiraTicketsStatusUpdates } from '../../../jira/api/jira-api';
import {
  findConnectorById,
  validateSingleConnector,
} from '../../../connectors/api/connectors-api';
import { loadDashboardInfo } from '../../../executive-summary/actions/executive-summary-actions';
import { isDevelopment } from 'env-utils';
import {
  atlasSearch,
  FilterElements,
  FilterTypeSearch,
} from '../../../common/filters-utils/filters-utils';
import isNull from 'lodash/isNull';
import { downloadTextToCsv } from '../../../common/common-utils';
import { parseBlob } from 'compress-utils';

export const createIssuesActions = (
  store: typeof IssuesStore | typeof ResolvedIssuesStore,
  service: typeof issuesService | typeof resolvedIssuesService,
  storeActions:
    | typeof issuesStoreActions
    | typeof resolvedIssuesStoreActions
    | typeof pipelineIssuesStoreActions,
  pageUrl: OneOfValues<typeof AppPages>,
) => {
  const loadIssues = createSimpleAsyncAction(
    async (params?: LoadIssuesParams) => {
      const {
        update = false,
        cache = true,
        limit = 100,
        exclude = false,
      } = params || {};

      if (update) {
        store.offset = 0;
      }
      if (exclude) {
        store.offset = 0;
        storeActions.setStoreIssues();
      }
      const { offset, filterIssuesBy, order, orderBy, totalIssues } =
        snapshot(store);

      const { scanID, isScanning, realScanId } = snapshot(ScanStore);
      const { dateRange } = snapshot(TopBarStore);
      const [from, to] = calcDateRange(dateRange);
      if (offset > totalIssues) return;
      const { displaySelectedAppOwners } = getAppOwnersStore();
      const { isDemoEnabled } = snapshot(ScanStore);
      const results = await service.getIssues.execute(
        {
          owners: displaySelectedAppOwners,
          offset,
          limit,
          ...(realScanId ? { scanID: realScanId } : scanID && { scanID }),
          filters: filterIssuesBy,
          sort: {
            fields: orderBy,
            order: order,
          },
          dateRange: {
            from,
            to,
          },
        },
        isDemoEnabled,
        cache === true || !isScanning,
      );
      if (results) {
        loadDashboardInfo();
        storeActions.setStoreIssues(results.issues, update);
        setStoreTopIssues(results.issues);
        storeActions.setStoreTotalIssuesFiltered(results.totalFilteredIssues);
        storeActions.setStoreTotalIssues(results.totalIssues);
        storeActions.setStoreTotalResolvedIssues(results.totalResolvedIssues);
        storeActions.setStoreTotalActiveIssues(results.totalActiveIssues);
        storeActions.setStoreIssuesOffset(results.offset);
      }
    },
    {
      asyncState: store.loading,
      errorMessage: 'Failed to load issues',
    },
  );

  const loadFilters = createSimpleAsyncAction(
    async (params?: LoadIssuesFiltersParams) => {
      const { cache = true, limit = 100, offset, search } = params || {};
      const { displaySelectedAppOwners } = getAppOwnersStore();
      const {
        offset: storeOffset,
        filterIssuesBy,
        searchValues,
      } = snapshot(store);
      const { dateRange } = snapshot(TopBarStore);
      const [from, to] = calcDateRange(dateRange);
      const { isDemoEnabled } = snapshot(ScanStore);
      const results = await service.getIssuesFilters.execute(
        {
          owners: displaySelectedAppOwners,
          offset: offset || storeOffset,
          limit,
          filters: filterIssuesBy,
          dateRange: { from, to },
          search: search || searchValues,
        },
        cache,
        isDemoEnabled,
      );

      if (results) {
        storeActions.setStoreIssuesFiltersType(results);
        storeActions.setStoreIssuesFiltersTypeSearch(results);
      }
    },
    {
      asyncState: store.loadingFilters,
      errorMessage: 'Failed to load issues filters',
    },
  );

  const loadFiltersWithoutSearch = createSimpleAsyncAction(
    async (params?: LoadIssuesFiltersParams) => {
      const { cache = true, limit = 100, offset } = params || {};
      const { displaySelectedAppOwners } = getAppOwnersStore();
      const { offset: storeOffset, filterIssuesBy } = snapshot(store);
      const { dateRange } = snapshot(TopBarStore);
      const [from, to] = calcDateRange(dateRange);
      const { isDemoEnabled } = snapshot(ScanStore);
      const results = await service.getIssuesFilters.execute(
        {
          owners: displaySelectedAppOwners,
          offset: offset || storeOffset,
          limit,
          filters: filterIssuesBy,
          dateRange: { from, to },
        },
        cache,
        isDemoEnabled,
      );

      if (results) {
        storeActions.setStoreIssuesFiltersType(results);
        storeActions.setStoreIssuesFiltersTypeSearch(results);
      }
    },
    {
      asyncState: store.loadingFilters,
      errorMessage: 'Failed to load issues filters',
    },
  );

  const loadStatistics = createSimpleAsyncAction(
    async (
      appName: string,
      cache: boolean = true,
      limit: number = 100,
      offset?: number,
    ) => {
      const { displaySelectedAppOwners } = getAppOwnersStore();
      const { offset: storeOffset, searchValues } = snapshot(store);
      const { dateRange } = snapshot(TopBarStore);
      const [from, to] = calcDateRange(dateRange);
      const { isDemoEnabled } = snapshot(ScanStore);
      const results = await service.getIssuesFilters.execute(
        {
          owners: displaySelectedAppOwners,
          offset: offset || storeOffset,
          limit,
          filters: { apps: [appName] },
          dateRange: { from, to },
          search: searchValues,
        },
        cache,
        isDemoEnabled,
      );

      if (results) {
        storeActions.setStoreIssuesStatistics(results);
      }
    },
    {
      asyncState: store.loadingFilters,
      errorMessage: 'Failed to load application statistics',
    },
  );

  const updateJiraTicketsLatestStatus = async () => {
    const jiraConnector = findConnectorById('4');
    const isActiveJiraConnection =
      jiraConnector?.isConfigured && (await validateSingleConnector('4', true));
    storeActions.setIsActiveJiraConnection(!!isActiveJiraConnection);
    const { selectedIssue } = snapshot(store);
    if (selectedIssue && isActiveJiraConnection) {
      storeActions.setLoadingJiraStatusUpdate(true);
      const jiraLatestStatusRes = await getJiraTicketsStatusUpdates(
        selectedIssue.tickets.map(t => t.key),
        selectedIssue.issueId,
        false,
      );
      if (jiraLatestStatusRes) {
        const jiraTicketStatus = jiraLatestStatusRes.map(
          ({ key, self, id, fields }) => ({
            link: self,
            ticketId: id,
            key,
            fields,
          }),
        );
        storeActions.setDrawerJiraTicketsStatus(jiraTicketStatus);
        storeActions.setJiraTicketsToRawIssue(
          selectedIssue.issueId,
          jiraTicketStatus,
        );
      }

      storeActions.setLoadingJiraStatusUpdate(false);
    }
  };

  const setIssueDrawer = async (issueId: string): Promise<void> => {
    const { selectedIssueId } = snapshot(store);
    if (issueId === selectedIssueId) return;
    storeActions.setSelectedIssueId(issueId);
    storeActions.setLoadingIssue(true);
    const result = await service.getIssue.execute({
      issueId,
    });
    if (result) {
      if (result?.exclusions) {
        setExclusionsCategories(result?.exclusions);
      }
      if (result && store.selectedIssueId === result.issueId) {
        trackAppcuesEvent(AppEvents.Issues.IssueSelected);
        storeActions.setSelectedIssue(result);
        setSelectedAggs([]);
        storeActions.setSelectedIssueId(result.issueId);
        storeActions.openIssueModal();
        updateJiraTicketsLatestStatus();
        isDevelopment() && validateActiveSlackConnection();
      }
    } else {
      openSnackbar(`Issue no longer exists`, {
        variant: 'error',
      });
    }
    storeActions.setLoadingIssue(false);
  };

  const clearSelectedIssue = () => {
    storeActions.setSelectedIssueId(null);
    storeActions.setSelectedIssue(null);
  };

  const setLoadingIssue = (value: boolean) => {
    store.loadingIssue = value;
  };

  const handleIssueRowClick = debounce((issueId: string) => {
    navigate(pageUrl, `?issueId=${issueId}`);
    setIssueDrawer(issueId);
  }, 500);

  const sortIssues = (key?: IssueSortKey, defaultSort?: IssueSortKey) => {
    const resetSort = !key;
    if (resetSort) {
      if (store.orderBy[0] === (defaultSort || IssueSortKey.Severity)) {
        store.order[0] =
          store.order[0] === IssuesOrder.Desc
            ? IssuesOrder.Asc
            : IssuesOrder.Desc;
      } else {
        store.order[0] = IssuesOrder.Desc;
        store.orderBy[0] = defaultSort || IssueSortKey.Severity;
      }
    } else {
      store.orderBy[0] = key;
      store.order[0] =
        store.order[0] === IssuesOrder.Asc ? IssuesOrder.Desc : IssuesOrder.Asc;
    }

    store.offset = 0;
    loadIssues({ update: true });
  };

  const handleSearchFilterType = (searchInput: string, filterType: string) => {
    if (searchInput === '') {
      const index = store.searchValues.findIndex(
        key => key.fieldName === filterType,
      );
      store.searchValues[index].value = '';
      loadFilters({});
      return;
    }
    if (atlasSearch(filterType) && isDevelopment()) {
      if (searchInput.length > 2) {
        storeActions.setSearchValues(filterType, searchInput);
        loadFilters();
      } else {
        storeActions.setSearchValues(filterType, searchInput);
        if (searchInput.length === 2) {
          loadFilters({ search: [] });
        }
      }
    } else {
      const { issuesFiltersType } = snapshot(store);
      if (issuesFiltersType && filterType) {
        const newFilteredTypes = issuesFiltersType[filterType].filter(
          (issueFilterType: FilterElements) =>
            issueFilterType.label
              .toLocaleLowerCase()
              .includes(searchInput.toLowerCase()),
        );
        storeActions.setSearchValues(filterType, searchInput);

        storeActions.setStoreIssuesFiltersTypeSearch({
          ...issuesFiltersType,
          [FilterTypeSearch[filterType]]: newFilteredTypes,
        });
      }
    }
  };

  const handleSearchFilterTypeWithoutAtlasSearch = (
    searchInput: string,
    filterType: string,
  ) => {
    if (searchInput === '') {
      const index = store.searchValues.findIndex(
        key => key.fieldName === filterType,
      );
      store.searchValues[index].value = '';
      loadFilters({});
      return;
    }
    const { issuesFiltersType } = snapshot(store);
    if (issuesFiltersType && filterType) {
      const newFilteredTypes = issuesFiltersType[filterType].filter(
        (issueFilterType: FilterElements) =>
          issueFilterType.label
            .toLocaleLowerCase()
            .includes(searchInput.toLowerCase()),
      );
      storeActions.setSearchValues(filterType, searchInput);

      storeActions.setStoreIssuesFiltersTypeSearch({
        ...issuesFiltersType,
        [FilterTypeSearch[filterType]]: newFilteredTypes,
      });
    }
  };

  const initIssues = async () => {
    const { offset, filterIssuesBy, order, orderBy } = snapshot(store);

    const { scanID, realScanId } = snapshot(ScanStore);
    const { dateRange } = snapshot(TopBarStore);
    const [from, to] = calcDateRange(dateRange);
    const { displaySelectedAppOwners } = getAppOwnersStore();
    const { isDemoEnabled } = snapshot(ScanStore);
    const results = await service.getIssues.execute(
      {
        offset,
        owners: displaySelectedAppOwners,
        limit: 1,
        ...(realScanId ? { scanID: realScanId } : scanID && { scanID }),
        filters: filterIssuesBy,
        sort: {
          fields: orderBy,
          order: order,
        },
        dateRange: {
          from,
          to,
        },
      },
      isDemoEnabled,
    );

    if (results) {
      const qs = getQueryString();
      const urlIssueId = qs.get('issueId');
      if (results.issues[0]) {
        if (store.selectedIssueId === null) {
          if (!urlIssueId) {
            setIssueDrawer(results.issues[0].issueId);
          } else {
            setIssueDrawer(urlIssueId);
          }
        }
      }
    }
  };

  const setMenuIssueId = (issueId: Nullable<Issue['issueId']>) => {
    store.menuIssueId = issueId;
  };

  const addingComment = async () => {
    if (!store.selectedIssue) {
      return;
    }
    try {
      setLoadingIssueComment(true);
      const result = await service.addCommentToIssue.execute({
        issueId: store.selectedIssue.issueId,
        comment: store.issueComment,
      });
      if (!result) {
        throw new Error(
          'Adding comment to issue failed due to an unknown error',
        );
      } else {
        issuesStoreActions.setCommentModal(false);
        issuesStoreActions.setIssueComment();
        openSnackbar('The comment was added successfully', {
          variant: 'success',
        });
      }
      setLoadingIssueComment(false);
      loadIssues({ update: true, cache: false });
    } catch (error) {
      issuesStoreActions.closeIssueModal();
      setLoadingIssueComment(false);
      openSnackbar(`Comment was not created, please try again`, {
        variant: 'error',
      });
      logger.error(`Issue comment failed due to an error: ${error}`);
    }
  };

  const setLoadingIssueComment = (loading: boolean) => {
    store.isAddingCommentLoading = loading;
  };

  const validateActiveSlackConnection = async () => {
    const jiraConnector = findConnectorById('97');
    const isActiveSlackConnection =
      jiraConnector?.isConfigured &&
      (await validateSingleConnector('97', true));
    storeActions.setIsActiveSlackConnection(!!isActiveSlackConnection);
  };

  const exportIssuesToCSVfile = async (includeIssuesFilters: boolean) => {
    try {
      const csvIssueSnackbarKey = 'download-csv-Issues-snackbar';

      openSnackbar(
        `Downloading Issues...`,
        {
          variant: 'default',
          persist: true,
          key: csvIssueSnackbarKey,
        },
        false,
        true,
      );
      const { filterIssuesBy, order, orderBy } = snapshot(store);
      const { scanID, realScanId } = snapshot(ScanStore);
      const { dateRange } = snapshot(TopBarStore);
      const [from, to] = calcDateRange(dateRange);
      const { displaySelectedAppOwners } = getAppOwnersStore();
      const scan = realScanId ? { scanID: realScanId } : scanID && { scanID };
      const exportedIssuesRes = await issuesService.getExportedIssues.execute(
        includeIssuesFilters
          ? {
              ...scan,
              filters: filterIssuesBy,
              sort: {
                fields: orderBy,
                order: order,
              },
              dateRange: {
                from,
                to,
              },
              owners: displaySelectedAppOwners,
            }
          : { ...scan },
      );
      if (isNull(exportedIssuesRes)) {
        throw new Error('Invalid response');
      }
      const { issuesCSV, fileName } = exportedIssuesRes;
      const csv = await parseBlob<{}>(issuesCSV);
      closeSnackbar(csvIssueSnackbarKey);

      downloadTextToCsv(csv as string, fileName);
    } catch (error) {
      logger.error(error);
      openSnackbar(
        `Opss exports to CSV file failed. We are on it, please try again later...`,
        {
          variant: 'warning',
        },
      );
    }
  };

  return {
    loadIssues,
    loadFilters,
    loadFiltersWithoutSearch,
    setIssueDrawer,
    clearSelectedIssue,
    setLoadingIssue,
    handleIssueRowClick,
    sortIssues,
    handleSearchFilterType,
    handleSearchFilterTypeWithoutAtlasSearch,
    initIssues,
    setMenuIssueId,
    loadStatistics,
    addingComment,
    setLoadingIssueComment,
    updateJiraTicketsLatestStatus,
    exportIssuesToCSVfile,
  };
};
